import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, View, Platform, NativeModules } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useToastStore } from '@/store';
import { API_HOST, GOOGLE_OAUTH_CLIENT_ID_WEB, GOOGLE_OAUTH_CLIENT_ID_IOS, APPLE_SSO_CLIENT_ID } from '@/constants/config';
import SettingsSection from '@/components/SettingsSection';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Svg, { Path } from 'react-native-svg';

const isWeb = Platform.OS === 'web';
const isIos = Platform.OS === 'ios';
const isMacOS = Platform.OS === 'macos';
const isAppleNative = isIos || isMacOS;
const EMPTY_PROVIDERS = [];

const GoogleLogo = ({ size = 18 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <Path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <Path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <Path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </Svg>
);

const AppleLogo = ({ size = 18, color = '#000' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.53-3.23 0-1.44.62-2.2.44-3.06-.4C3.79 16.17 4.36 9.53 8.82 9.28c1.28.06 2.15.72 2.9.75.99-.2 1.94-.89 3.01-.8 1.28.1 2.24.63 2.87 1.6-2.63 1.56-2 5 .82 5.97-.62 1.65-1.43 3.26-2.37 4.48zM12.59 9.16c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill={color} />
  </Svg>
);

const createStyles = (colors) => StyleSheet.create({
  providerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border_color_secondary,
  },
  providerRowLast: {
    borderBottomWidth: 0,
  },
  providerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  providerIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
  },
  providerTextContainer: {
    gap: 2,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});

async function updateConnectedAccount(endpoint, body) {
  const token = useAuthStore.getState().token;
  const response = await fetch(`${API_HOST}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.error) {
    throw new Error(data.message || 'Request failed');
  }

  // The mutation returns fresh account details; /json/user/me can still be
  // cached. Merge only account fields so populated profile data stays intact.
  const user = data.user;
  if (user?.id) {
    useAuthStore.setState(state => {
      if (!state.token || state.user?.id !== user.id) return state;
      return {
        user: {
          ...state.user,
          connectedProviders: user.connectedProviders,
          hasPassword: user.hasPassword,
          authProvider: user.authProvider,
          email: user.email,
          flags: { ...state.user.flags, isEmailVerified: user.flags?.isEmailVerified },
        },
      };
    });
  }
  return data;
}

function useEnsureWebSSOScripts() {
  useEffect(() => {
    if (!isWeb) return;

    // Load Google GIS if not already present
    const GOOGLE_CLIENT_ID = GOOGLE_OAUTH_CLIENT_ID_WEB;
    if (GOOGLE_CLIENT_ID && !document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.google?.accounts?.id?.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            window.__gabGoogleLinkCallback?.(response);
          },
          use_fedcm_for_prompt: true,
        });
      };
      document.head.appendChild(script);
    }

    // Load Apple Sign-In JS if not already present
    const APPLE_CLIENT_ID = APPLE_SSO_CLIENT_ID;
    if (APPLE_CLIENT_ID && !document.getElementById('apple-signin-script')) {
      const script = document.createElement('script');
      script.id = 'apple-signin-script';
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.AppleID?.auth?.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: window.location.origin,
          usePopup: true,
        });
      };
      document.head.appendChild(script);
    }
  }, []);
}

function ConnectedAccountsSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { addToast } = useToastStore(state => state.actions);

  const connectedProviders = useAuthStore(state => state.user?.connectedProviders || EMPTY_PROVIDERS);
  const hasPassword = useAuthStore(state => state.user?.hasPassword);

  const [loading, setLoading] = useState(null);

  useEnsureWebSSOScripts();

  const isGoogleLinked = connectedProviders.includes('google');
  const isAppleLinked = connectedProviders.includes('apple');

  const authMethodCount = (hasPassword ? 1 : 0) + (isGoogleLinked ? 1 : 0) + (isAppleLinked ? 1 : 0);

  const handleLinkGoogle = useCallback(async () => {
    try {
      setLoading('link-google');

      if (isWeb) {
        if (!window.google?.accounts?.id) {
          addToast(t('settings.connectedAccounts.errors.googleNotAvailable'), 'error');
          setLoading(null);
          return;
        }
        const GOOGLE_CLIENT_ID = GOOGLE_OAUTH_CLIENT_ID_WEB;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            try {
              await updateConnectedAccount('/json/account/link-google', { idToken: response.credential });
              addToast(t('settings.connectedAccounts.googleLinked'), 'success');
            } catch (err) {
              addToast(err.message, 'error');
            } finally {
              setLoading(null);
            }
          },
          use_fedcm_for_prompt: true,
        });
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            setLoading(null);
            addToast(t('settings.connectedAccounts.errors.googleNotAvailable'), 'error');
          }
        });
        return;
      }

      // macOS: @react-native-google-signin/google-signin has no macOS
      // support — its TurboModule registration crashes the app at require
      // time. Use the native MacGoogleAuth bridge instead, mirroring the
      // login flow in SSOButtons.
      if (isMacOS) {
        const credential = await NativeModules.MacGoogleAuth?.signIn({
          iosClientId: GOOGLE_OAUTH_CLIENT_ID_IOS,
          scopes: 'openid email profile',
        });
        if (!credential?.idToken) {
          throw new Error(t('settings.connectedAccounts.errors.linkFailed'));
        }
        await updateConnectedAccount('/json/account/link-google', { idToken: credential.idToken });
        addToast(t('settings.connectedAccounts.googleLinked'), 'success');
        return;
      }

      // iOS / Android: use @react-native-google-signin/google-signin.
      const { GoogleSignin } = require('@react-native-google-signin/google-signin');
      if (Platform.OS === 'android') {
        await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
      }
      const signInResult = await GoogleSignin.signIn();
      if (signInResult?.type === 'cancelled') {
        return;
      }
      if (signInResult?.type !== 'success' || !signInResult.data) {
        throw new Error(t('settings.connectedAccounts.errors.linkFailed'));
      }
      let idToken = signInResult.data.idToken;
      if (!idToken) {
        const tokens = await GoogleSignin.getTokens();
        idToken = tokens?.idToken;
      }
      if (!idToken) throw new Error('No ID token');

      await updateConnectedAccount('/json/account/link-google', { idToken });
      addToast(t('settings.connectedAccounts.googleLinked'), 'success');
    } catch (err) {
      if (err?.code !== '-5' && err?.code !== 'SIGN_IN_CANCELLED' && err?.code !== 'ERR_REQUEST_CANCELED') {
        addToast(err.message || t('settings.connectedAccounts.errors.linkFailed'), 'error');
      }
    } finally {
      setLoading(null);
    }
  }, [addToast, t]);

  const handleLinkApple = useCallback(async () => {
    try {
      setLoading('link-apple');

      if (isWeb) {
        const response = await window.AppleID?.auth?.signIn();
        const idToken = response?.authorization?.id_token;
        if (!idToken) throw new Error('No identity token');
        await updateConnectedAccount('/json/account/link-apple', { idToken });
        addToast(t('settings.connectedAccounts.appleLinked'), 'success');
        return;
      }

      if (!isAppleNative) return;

      if (isMacOS) {
        const originalAccount = useAuthStore.getState();
        const accountId = originalAccount.user?.id;
        const accountToken = originalAccount.token;
        const credential = await NativeModules.MacAppleAuth?.signIn({ apiOrigin: API_HOST });
        const currentAccount = useAuthStore.getState();
        if (currentAccount.user?.id !== accountId || currentAccount.token !== accountToken) {
          throw new Error('Your account changed during Apple sign-in. Please try again.');
        }
        if (!credential?.identityToken) throw new Error('No identity token');
        await updateConnectedAccount('/json/account/link-apple', { idToken: credential.identityToken });
        addToast(t('settings.connectedAccounts.appleLinked'), 'success');
        return;
      }

      const AppleAuthentication = require('expo-apple-authentication');

      const isAvailable = await AppleAuthentication.isAvailableAsync();
      if (!isAvailable) {
        throw new Error('Apple Sign In is not available on this device');
      }

      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });
      if (!credential.identityToken) throw new Error('No identity token');

      await updateConnectedAccount('/json/account/link-apple', { idToken: credential.identityToken });
      addToast(t('settings.connectedAccounts.appleLinked'), 'success');
    } catch (err) {
      if (err?.code !== 'ERR_REQUEST_CANCELED' && err?.error !== 'popup_closed_by_user') {
        addToast(err.message || t('settings.connectedAccounts.errors.linkFailed'), 'error');
      }
    } finally {
      setLoading(null);
    }
  }, [addToast, t]);

  const handleUnlink = useCallback(async (provider) => {
    try {
      setLoading(`unlink-${provider}`);
      await updateConnectedAccount('/json/account/unlink-provider', { provider });
      addToast(t('settings.connectedAccounts.providerUnlinked').replace('%PROVIDER%', provider === 'google' ? 'Google' : 'Apple'), 'success');
    } catch (err) {
      addToast(err.message || t('settings.connectedAccounts.errors.unlinkFailed'), 'error');
    } finally {
      setLoading(null);
    }
  }, [addToast, t]);

  const showApple = isAppleNative || isWeb;

  return (
    <View>
      <SettingsSection
        title={t('settings.connectedAccounts.title')}
        subtitle={t('settings.connectedAccounts.subtitle')}
        isBold
        isColumn
        isFirst
        isLast
      >
        {/* Google */}
        <View style={[styles.providerRow, !showApple && styles.providerRowLast]}>
          <View style={styles.providerInfo}>
            <View style={styles.providerIconContainer}>
              <GoogleLogo size={18} />
            </View>
            <View style={styles.providerTextContainer}>
              <Text size="m" weight="medium">Google</Text>
              {isGoogleLinked ? (
                <View style={styles.statusBadge}>
                  <Icon name="circle-check" size={12} color={colors.color_success} />
                  <Text size="xs" color="secondary">{t('settings.connectedAccounts.connected')}</Text>
                </View>
              ) : (
                <Text size="xs" color="tertiary">{t('settings.connectedAccounts.notConnected')}</Text>
              )}
            </View>
          </View>
          {isGoogleLinked ? (
            <Button
              text={t('settings.connectedAccounts.unlink')}
              variant="danger"
              size="small"
              onPress={() => handleUnlink('google')}
              isLoading={loading === 'unlink-google'}
              isDisabled={!!loading || authMethodCount <= 1}
            />
          ) : (
            <Button
              text={t('settings.connectedAccounts.link')}
              variant="secondary"
              size="small"
              onPress={handleLinkGoogle}
              isLoading={loading === 'link-google'}
              isDisabled={!!loading}
            />
          )}
        </View>

        {/* Apple */}
        {showApple && (
          <View style={[styles.providerRow, styles.providerRowLast]}>
            <View style={styles.providerInfo}>
              <View style={styles.providerIconContainer}>
                <AppleLogo size={18} color={colors.text_color_primary} />
              </View>
              <View style={styles.providerTextContainer}>
                <Text size="m" weight="medium">Apple</Text>
                {isAppleLinked ? (
                  <View style={styles.statusBadge}>
                    <Icon name="circle-check" size={12} color={colors.color_success} />
                    <Text size="xs" color="secondary">{t('settings.connectedAccounts.connected')}</Text>
                  </View>
                ) : (
                  <Text size="xs" color="tertiary">{t('settings.connectedAccounts.notConnected')}</Text>
                )}
              </View>
            </View>
            {isAppleLinked ? (
              <Button
                text={t('settings.connectedAccounts.unlink')}
                variant="danger"
                size="small"
                onPress={() => handleUnlink('apple')}
                isLoading={loading === 'unlink-apple'}
                isDisabled={!!loading || authMethodCount <= 1}
              />
            ) : (
              <Button
                text={t('settings.connectedAccounts.link')}
                variant="secondary"
                size="small"
                onPress={handleLinkApple}
                isLoading={loading === 'link-apple'}
                isDisabled={!!loading}
              />
            )}
          </View>
        )}

        {authMethodCount <= 1 && (isGoogleLinked || isAppleLinked) && (
          <Text size="xs" color="tertiary" style={{ marginTop: 8 }}>
            {t('settings.connectedAccounts.cannotUnlinkOnly')}
          </Text>
        )}
      </SettingsSection>
    </View>
  );
}

export default React.memo(ConnectedAccountsSettings);
